import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Shield, Zap, Cloud, Users, Settings, TrendingUp } from 'lucide-react';

const Home = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'IT-Dienstleistungen & IT-Beratung Mönchengladbach – Schteffann',
    description: 'IT-Dienstleistungen & IT-Beratung Mönchengladbach – IT-Systemadministrator, Microsoft 365, Digitalisierung, IT-Sicherheit für KMU.',
    url: 'https://schteffann.de/',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://schteffann.de/' }]
    }
  };

  const kernversprechen = [
    {
      icon: Cloud,
      title: 'Microsoft 365 sinnvoll nutzen',
      text: 'Viele Unternehmen zahlen für Microsoft 365, nutzen aber nur einen Bruchteil. Ich helfe Ihnen, die Tools zu verstehen und gewinnbringend einzusetzen – ohne unnötige Komplexität.'
    },
    {
      icon: Shield,
      title: 'IT-Sicherheit ohne Panik',
      text: 'Sicherheit muss nicht kompliziert sein. Ich zeige Ihnen, welche Maßnahmen wirklich wichtig sind und wie Sie Ihre Daten schützen – realistisch und umsetzbar.'
    },
    {
      icon: Zap,
      title: 'Prozesse vereinfachen',
      text: 'Digitalisierung bedeutet nicht, alles umzukrempeln. Ich helfe Ihnen, Arbeitsabläufe Schritt für Schritt zu verbessern – dort, wo es sich wirklich lohnt.'
    }
  ];

  const probleme = [
    'Microsoft 365 läuft, aber niemand weiß so richtig, was man damit machen kann',
    'Zu viele verschiedene Tools, keine klare Struktur',
    'Unsicherheit bei Datenschutz und IT-Sicherheit',
    'Jeder arbeitet anders – keine einheitlichen Prozesse',
    'IT-Dienstleister sprechen eine andere Sprache',
    'Keine Zeit, sich in komplizierte Systeme einzuarbeiten'
  ];

  const leistungen = [
    { icon: Cloud, title: 'Microsoft 365 Optimierung', url: '/microsoft-365-optimierung', text: 'Das Beste aus Ihrem Microsoft 365 herausholen' },
    { icon: TrendingUp, title: 'Digitalisierung & Prozesse', url: '/digitalisierung-prozessoptimierung', text: 'Arbeitsabläufe vereinfachen und Zeit sparen' },
    { icon: Settings, title: 'IT-Infrastruktur', url: '/it-infrastruktur-kmu', text: 'Stabile, sichere IT für den Alltag' },
    { icon: Shield, title: 'IT-Sicherheit & ISMS', url: '/it-sicherheit-isms', text: 'Grundlegende Sicherheit ohne Übertreibung' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="IT-Dienstleistungen & IT-Beratung Mönchengladbach – Schteffann | KMU IT-Support"
        description="IT-Dienstleistungen & IT-Beratung Mönchengladbach ✓ IT-Systemadministrator ✓ Microsoft 365 ✓ Digitalisierung ✓ IT-Sicherheit ✓ Pragmatisch & ehrlich für KMU in Mönchengladbach, Viersen, Krefeld."
        keywords="IT-Dienstleistungen Mönchengladbach, IT-Beratung Mönchengladbach, IT-Systemadministrator Mönchengladbach, Microsoft 365 Beratung, IT Support KMU, Digitalisierung Handwerk, IT-Sicherheit KMU, Interim IT-Leiter, Systemadministration, SharePoint, Teams, IT-Infrastruktur, Stephan Schunter, Schteffann, Viersen, Krefeld, Neuss, Düsseldorf, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">IT-Unterstützung für KMU in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach & Umgebung</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed mb-8">
              Ich helfe Ihnen, Microsoft 365 sinnvoll zu nutzen, Ihre IT-Prozesse zu vereinfachen und Ihre Daten zu schützen. Pragmatisch, verständlich, ohne unnötigen Schnickschnack.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white shadow-xl px-8 py-4 text-lg">
                <Link to="/contact">
                  <Target className="mr-2 h-5 w-5" />
                  Kostenloses Erstgespräch
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="border-2 border-cyan-600 text-cyan-700 hover:bg-cyan-50 px-8 py-4 text-lg">
                <Link to="/services">
                  Leistungen ansehen
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Kernversprechen */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Worum es mir geht
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Keine großen Versprechen, keine Marketing-Phrasen. Nur ehrliche Unterstützung für Ihre IT.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {kernversprechen.map((item, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl mb-6 group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                    <item.icon className="h-8 w-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Kommt Ihnen das bekannt vor?
              </h2>
              <p className="text-xl text-slate-600">
                Typische Situationen in kleinen und mittleren Unternehmen:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {probleme.map((problem, index) => (
                <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-cyan-600 mt-1 flex-shrink-0" />
                  <p className="text-slate-700">{problem}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-lg text-slate-700 mt-8">
              Falls ja – Sie sind nicht allein. Und es gibt pragmatische Lösungen.
            </p>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Wie ich Ihnen helfen kann
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Meine Schwerpunkte – immer mit Fokus auf das, was für Ihr Unternehmen wirklich wichtig ist.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {leistungen.map((item, index) => (
              <Link key={index} to={item.url}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-2">
                          {item.title}
                        </h3>
                        <p className="text-slate-600">{item.text}</p>
                      </div>
                    </div>
                    <div className="flex items-center text-cyan-600 group-hover:text-cyan-700 mt-4">
                      <span className="text-sm font-medium">Mehr erfahren</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" asChild className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white shadow-xl px-8 py-4 text-lg">
              <Link to="/services">
                Alle Leistungen ansehen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Einsatzgebiet */}
      <section className="py-12">
        <div className="container">
          <Card className="border-2 border-cyan-100 bg-cyan-50/30 max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="text-center">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Einsatzgebiet</h2>
                <p className="text-slate-700">
                  Vor-Ort-Support im Raum Mönchengladbach (ca. 20 km Radius):<br />
                  <strong>Mönchengladbach · Viersen · Krefeld · Neuss · Korschenbroich · Jüchen · Düsseldorf</strong><br />
                  Remote-Support ist jederzeit und bundesweit möglich.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Lassen Sie uns über Ihre IT sprechen
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                In einem kostenlosen Erstgespräch klären wir, wo Sie stehen und was sinnvolle nächste Schritte wären. Unverbindlich und ehrlich.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;